import { clientApiScope, Constants } from "../../../services/web/api.const";
import { useApi, useApiAsync } from "../../../services/web/use-api";
import { CreateFeeConfigInput, FeeConfig, UpdateFeeConfigInput } from "../fee-config.type";
import { ParkingRateType } from "../payment-zone-area.type";

const feeConfigsUrl = (cityId: string) => `${Constants.CLIENTS_API_URL}/cities/${cityId}/fee-configs`;

const jsonHeaders = { "Content-type": "application/json; charset=UTF-8", "api-version": "v2" };

export const useGetFeeConfigs = (cityId: string) => {
  const options = { ...clientApiScope, method: "GET", headers: jsonHeaders };

  const { isLoading, data, error } = useApi<FeeConfig[]>(
    feeConfigsUrl(cityId),
    options,
    ["fee-configs", cityId],
    !!cityId,
  );

  return { isLoading, feeConfigs: data ?? [], error };
};

export const useCreateFeeConfig = (cityId: string) => {
  const options = { ...clientApiScope, method: "POST", headers: jsonHeaders };

  const {
    mutateAsync: createFeeConfig,
    isPending: isLoading,
    error,
  } = useApiAsync<FeeConfig, CreateFeeConfigInput>(feeConfigsUrl(cityId), options, ["fee-configs", cityId]);

  return { createFeeConfig, isLoading, error };
};

export const useUpdateFeeConfig = (cityId: string) => {
  const options = {
    ...clientApiScope,
    method: "PATCH",
    headers: jsonHeaders,
    toBody: (vars: { id: string; input: UpdateFeeConfigInput }) => vars.input,
  };

  const {
    mutateAsync: updateFeeConfig,
    isPending: isLoading,
    error,
  } = useApiAsync<FeeConfig, { id: string; input: UpdateFeeConfigInput }>(
    (vars) => `${feeConfigsUrl(cityId)}/${vars.id}`,
    options,
    ["fee-configs", cityId],
  );

  return { updateFeeConfig, isLoading, error };
};

const useFeeConfigAction = (cityId: string, action: string) => {
  const options = { ...clientApiScope, method: "POST", headers: jsonHeaders, toBody: () => undefined };

  return useApiAsync<FeeConfig, string>((feeConfigId) => `${feeConfigsUrl(cityId)}/${feeConfigId}/${action}`, options, [
    "fee-configs",
    cityId,
  ]);
};

export const useCreateFeeConfigVersion = (cityId: string) => {
  const { mutateAsync: createFeeConfigVersion, isPending: isLoading, error } = useFeeConfigAction(cityId, "versions");
  return { createFeeConfigVersion, isLoading, error };
};

export const usePublishFeeConfig = (cityId: string) => {
  const { mutateAsync: publishFeeConfig, isPending: isLoading, error } = useFeeConfigAction(cityId, "publish");
  return { publishFeeConfig, isLoading, error };
};

export const useDisableFeeConfig = (cityId: string) => {
  const { mutateAsync: disableFeeConfig, isPending: isLoading, error } = useFeeConfigAction(cityId, "disable");
  return { disableFeeConfig, isLoading, error };
};

export const usePropagateFeeConfig = (cityId: string) => {
  const options = { ...clientApiScope, method: "POST", headers: jsonHeaders, toBody: () => undefined };

  const {
    mutateAsync: propagateFeeConfig,
    isPending: isLoading,
    error,
  } = useApiAsync<ParkingRateType[], string>(
    (feeConfigId) => `${feeConfigsUrl(cityId)}/${feeConfigId}/propagate`,
    options,
    ["parking-rates"],
  );

  return { propagateFeeConfig, isLoading, error };
};

export const useDeleteFeeConfig = (cityId: string) => {
  const options = { ...clientApiScope, method: "DELETE", headers: jsonHeaders, toBody: () => undefined };

  const {
    mutateAsync: deleteFeeConfig,
    isPending: isLoading,
    error,
  } = useApiAsync<void, string>((feeConfigId) => `${feeConfigsUrl(cityId)}/${feeConfigId}`, options, [
    "fee-configs",
    cityId,
  ]);

  return { deleteFeeConfig, isLoading, error };
};
